<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="payments-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('payments.title') }}</h1>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <div class="filter-input-wrapper search-input">
          <IntercomInput
            v-model="searchQuery"
            :placeholder="$t('members.searchPlaceholder')"
            @input="debouncedSearch"
          />
          <button v-if="searchQuery" class="clear-btn" @click="clearSearch" aria-label="Clear search">×</button>
        </div>

        <div class="date-filters">
          <div class="date-field">
            <IntercomDateInput
              v-model="dateFrom"
              :label="$t('payments.fromDate')"
              @update:model-value="resetAndFetch(currentFilters())"
            />
            <button v-if="dateFrom !== defaultFromDate" class="clear-btn" @click="clearFromDate" aria-label="Reset from date">×</button>
          </div>
          <div class="date-field">
            <IntercomDateInput
              v-model="dateTo"
              :label="$t('payments.toDate')"
              @update:model-value="resetAndFetch(currentFilters())"
            />
            <button v-if="dateTo !== defaultToDate" class="clear-btn" @click="clearToDate" aria-label="Reset to date">×</button>
          </div>
        </div>

        <button v-if="hasActiveFilters" class="reset-all-btn" @click="resetAllFilters">
          {{ $t('common.resetAll') || 'Reset all' }}
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Empty State -->
      <div v-else-if="payments.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        </div>
        <h3>{{ $t('payments.noPayments') }}</h3>
      </div>

      <!-- Payments Table -->
      <IntercomCard v-else class="payments-table-card">
        <table class="payments-table">
          <thead>
            <tr>
              <th>{{ $t('payments.receiptNumber') }}</th>
              <th>{{ $t('payments.member') }}</th>
              <th>{{ $t('payments.paymentDate') }}</th>
              <th>{{ $t('members.plan') }}</th>
              <th>{{ $t('payments.amount') }}</th>
              <th>{{ $t('payments.paymentMode') }}</th>
              <th>{{ $t('payments.validity') }}</th>
              <th>{{ $t('payments.status') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="payment in payments"
              :key="payment.id"
              :class="{ voided: payment.status === 'void' }"
            >
              <td class="receipt-number">{{ payment.receipt_number }}</td>
              <td class="member-name" @click="goToMember(payment.member_id)">
                {{ payment.member_name }}
              </td>
              <td>{{ formatDate(payment.payment_date) }}</td>
              <td>{{ payment.plan_name }}</td>
              <td class="amount">{{ formatCurrency(payment.amount) }}</td>
              <td class="payment-mode">{{ payment.payment_mode }}</td>
              <td class="validity">
                {{ formatDate(payment.membership_start) }} - {{ formatDate(payment.membership_end) }}
              </td>
              <td>
                <span class="status-badge" :class="payment.status">
                  {{ $t(`payments.status${capitalize(payment.status)}`) }}
                </span>
              </td>
              <td class="actions">
                <IntercomButton
                  variant="ghost"
                  size="sm"
                  @click="openPrintModal(payment)"
                >
                  {{ $t('common.print') }}
                </IntercomButton>
                <IntercomButton
                  v-if="payment.status === 'active'"
                  variant="ghost"
                  size="sm"
                  @click="openVoidModal(payment)"
                >
                  {{ $t('payments.voidPayment') }}
                </IntercomButton>
                <IntercomButton
                  v-if="payment.status === 'active'"
                  variant="ghost"
                  size="sm"
                  @click="handleResendReceipt(payment)"
                >
                  {{ $t('payments.resendReceipt') }}
                </IntercomButton>
              </td>
            </tr>
          </tbody>
        </table>
        <IntercomPagination
          :total="total"
          :limit="limit"
          :offset="offset"
          :item-label="$t('payments.title').toLowerCase()"
          @update:offset="onPageChange"
        />
      </IntercomCard>

      <!-- Void Modal -->
      <IntercomModal v-model:show="showVoidModal" :title="$t('payments.voidPayment')">
        <form @submit.prevent="handleVoidPayment" class="void-form">
          <p class="void-warning">{{ $t('payments.voidConfirm') }}</p>

          <div class="form-field">
            <IntercomInput
              v-model="voidReason"
              :label="$t('payments.voidReason')"
              :placeholder="$t('payments.voidReasonPlaceholder')"
              required
            />
          </div>

          <div v-if="voidError" class="error-message">
            {{ voidError }}
          </div>

          <div class="modal-actions">
            <IntercomButton variant="secondary" type="button" @click="closeVoidModal">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton variant="danger" type="submit" :loading="voidLoading">
              {{ $t('payments.voidPayment') }}
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>

      <!-- Print Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        :title="$t('receipt.printPreview')"
        size="lg"
      >
        <div class="print-modal-content">
          <div class="print-preview-container">
            <GymReceiptPrint
              v-if="printingPayment"
              :payment="printingPayment"
              :member="printingPayment"
              :business-name="businessName"
              :business-address="user?.business?.address"
              :business-phone="user?.business?.phone"
              :business-email="user?.business?.email"
            />
          </div>
          <div class="print-actions">
            <IntercomButton variant="primary" @click="handlePrint">
              {{ $t('common.print') }}
            </IntercomButton>
            <IntercomButton variant="ghost" @click="showPrintModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner, IntercomModal, IntercomPagination, IntercomDateInput } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import GymReceiptPrint from '../components/print/GymReceiptPrint.vue'
import dayjs from 'dayjs'

const router = useRouter()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { listPayments, voidPayment, resendReceipt, globalSearch } = useGym()

const { navSections } = useNavigation()

// Default filter values
const defaultFromDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD')
const defaultToDate = dayjs().format('YYYY-MM-DD')

// State
const searchQuery = ref('')
const dateFrom = ref(defaultFromDate)
const dateTo = ref(defaultToDate)

// Paginated list
const { items: payments, total, loading, offset, limit, fetch: fetchPayments, resetAndFetch } = usePaginatedList(
  (params) => listPayments({ ...params, include_void: true }),
  { pageSize: 25, immediate: false }
)

// Helper to build current filter params
const currentFilters = () => ({
  search: searchQuery.value || undefined,
  from_date: dateFrom.value || undefined,
  to_date: dateTo.value || undefined,
})

// Initial fetch with default date range
resetAndFetch(currentFilters())

// Computed - check if any filter is active
const hasActiveFilters = computed(() =>
  searchQuery.value !== '' ||
  dateFrom.value !== defaultFromDate ||
  dateTo.value !== defaultToDate
)

// Void modal state
const showVoidModal = ref(false)
const voidingPayment = ref(null)
const voidReason = ref('')
const voidLoading = ref(false)
const voidError = ref('')

// Print modal state
const showPrintModal = ref(false)
const printingPayment = ref(null)

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const formatDate = (date) => {
  if (!date) return '-'
  return dayjs(date).format('DD MMM YYYY')
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

let searchTimeout = null
const debouncedSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => resetAndFetch(currentFilters()), 300)
}

// Filter clear methods
const clearSearch = () => {
  searchQuery.value = ''
  resetAndFetch(currentFilters())
}

const clearFromDate = () => {
  dateFrom.value = defaultFromDate
  resetAndFetch(currentFilters())
}

const clearToDate = () => {
  dateTo.value = defaultToDate
  resetAndFetch(currentFilters())
}

const resetAllFilters = () => {
  searchQuery.value = ''
  dateFrom.value = defaultFromDate
  dateTo.value = defaultToDate
  resetAndFetch(currentFilters())
}

const onPageChange = (newOffset) => {
  offset.value = newOffset
  fetchPayments()
}

const goToMember = (memberId) => {
  router.push(`/members/${memberId}`)
}

const openVoidModal = (payment) => {
  voidingPayment.value = payment
  voidReason.value = ''
  voidError.value = ''
  showVoidModal.value = true
}

const closeVoidModal = () => {
  showVoidModal.value = false
  voidingPayment.value = null
}

const handleVoidPayment = async () => {
  try {
    voidLoading.value = true
    voidError.value = ''

    await voidPayment(voidingPayment.value.id, voidReason.value)

    closeVoidModal()
    await fetchPayments()
  } catch (error) {
    console.error('Failed to void payment:', error)
    voidError.value = error.message || t('errors.generic')
  } finally {
    voidLoading.value = false
  }
}

const handleResendReceipt = async (payment) => {
  try {
    await resendReceipt(payment.id)
    // Show success message (could use toast)
    alert('Receipt sent successfully')
  } catch (error) {
    console.error('Failed to resend receipt:', error)
    alert(error.message || 'Failed to send receipt')
  }
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

// Print modal methods
const openPrintModal = (payment) => {
  printingPayment.value = payment
  showPrintModal.value = true
}

const handlePrint = () => {
  // Add print class to body to hide non-print content
  document.body.classList.add('printing-receipt')
  window.print()
  document.body.classList.remove('printing-receipt')
}

</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.payments-view {
  padding: var(--space-6);
  max-width: 1600px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.filters-bar {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-input-wrapper.search-input {
  width: 300px;
  max-width: 300px;
  flex-shrink: 0;
}

.filter-input-wrapper.search-input :deep(.intercom-input-wrapper) {
  width: 100%;
}

.date-filters {
  display: flex;
  gap: var(--space-4);
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.date-field label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.date-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
}

.date-input:focus {
  outline: none;
  border-color: var(--color-blue);
}

/* Filter input wrapper with clear button */
.filter-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.filter-input-wrapper .date-input {
  padding-right: var(--space-8);
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--color-gray-200);
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  color: var(--color-gray-600);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.15s, color 0.15s;
}

.clear-btn:hover {
  background: var(--color-gray-300);
  color: var(--color-charcoal);
}

.reset-all-btn {
  background: none;
  border: none;
  color: var(--color-blue);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  padding: var(--space-2) var(--space-3);
  white-space: nowrap;
  align-self: flex-end;
}

.reset-all-btn:hover {
  text-decoration: underline;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.empty-state {
  text-align: center;
  padding: var(--space-12);
}

.empty-icon {
  color: var(--color-gray-300);
  margin-bottom: var(--space-4);
}

.empty-state h3 {
  font-size: var(--font-size-lg);
  color: var(--color-charcoal);
  margin: 0;
}

.payments-table-card {
  overflow: hidden;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table th {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
  white-space: nowrap;
}

.payments-table td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);
}

.payments-table tr.voided {
  opacity: 0.6;
}

.payments-table tr.voided td {
  text-decoration: line-through;
}

.receipt-number {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.member-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  cursor: pointer;
}

.member-name:hover {
  text-decoration: underline;
}

.amount {
  font-weight: var(--font-weight-medium);
  color: var(--color-success);
}

.payment-mode {
  text-transform: capitalize;
}

.validity {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.status-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.status-badge.active {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.status-badge.void {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.actions {
  white-space: nowrap;
}

/* Void Modal */
.void-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.void-warning {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  margin: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.error-message {
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
}

/* Print Modal */
.print-modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.print-preview-container {
  max-height: 70vh;
  overflow-y: auto;
  background: var(--color-gray-100);
  padding: var(--space-4);
  border-radius: var(--radius-md);
}

.print-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

@media (max-width: 768px) {
  .payments-view {
    padding: var(--space-4);
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }

  .date-filters {
    flex-direction: column;
  }

  .payments-table-card {
    overflow-x: auto;
  }
}

/* Print styles - hide everything except receipt when printing */
@media print {
  .payments-view,
  .page-header,
  .filters-bar,
  .payments-table-card,
  .print-actions {
    display: none !important;
  }

  .print-preview-container {
    background: white !important;
    padding: 0 !important;
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
